import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AnonymousContext = createContext();

export function useAnonymous() {
  const context = useContext(AnonymousContext);
  if (!context) {
    throw new Error('useAnonymous must be used within an AnonymousProvider');
  }
  return context;
}

const STORAGE_KEY = 'anonymous_session';
const DAILY_LIMIT = 3;

export function AnonymousProvider({ children }) {
  // SSR guard
  const isBrowser = typeof window !== 'undefined';
  
  const [sessionData, setSessionData] = useState({
    roundsToday: 0,
    date: new Date().toLocaleDateString(),
    sessionId: null
  });

  // Load session data from localStorage (client-side only)
  useEffect(() => {
    if (!isBrowser) return;
    
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const today = new Date().toLocaleDateString();
        
        // Reset if it's a new day
        if (parsed.date !== today) {
          const newData = {
            roundsToday: 0,
            date: today,
            sessionId: generateSessionId()
          };
          setSessionData(newData);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
        } else {
          setSessionData(parsed);
        }
      } catch (error) {
        console.error('Failed to parse anonymous session:', error);
        resetSession();
      }
    } else {
      resetSession();
    }
  }, [isBrowser]);

  // Generate unique session ID
  const generateSessionId = () => {
    return `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Reset session (new day or error)
  const resetSession = useCallback(() => {
    if (!isBrowser) return;
    
    const newData = {
      roundsToday: 0,
      date: new Date().toLocaleDateString(),
      sessionId: generateSessionId()
    };
    setSessionData(newData);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
  }, [isBrowser]);

  // Increment round count
  const incrementRounds = useCallback(() => {
    if (!isBrowser) return sessionData;
    
    const today = new Date().toLocaleDateString();
    
    // Check if we need to reset (new day)
    if (sessionData.date !== today) {
      resetSession();
      const newData = {
        roundsToday: 1,
        date: today,
        sessionId: generateSessionId()
      };
      setSessionData(newData);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
      return newData;
    }
    
    // Increment current day
    const newData = {
      ...sessionData,
      roundsToday: sessionData.roundsToday + 1
    };
    setSessionData(newData);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
    return newData;
  }, [sessionData, resetSession, isBrowser]);
  
  // Set round count directly (for syncing with backend)
  const setRoundsCount = useCallback((count) => {
    const today = new Date().toLocaleDateString();
    const newData = {
      ...sessionData,
      roundsToday: count,
      date: today
    };
    setSessionData(newData);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
  }, [sessionData]);

  // Check if limit reached
  const hasReachedLimit = useCallback(() => {
    const today = new Date().toLocaleDateString();
    
    // Reset if new day
    if (sessionData.date !== today) {
      return false;
    }
    
    return sessionData.roundsToday >= DAILY_LIMIT;
  }, [sessionData]);

  // Get remaining rounds
  const getRemainingRounds = useCallback(() => {
    const today = new Date().toLocaleDateString();
    
    // Reset if new day
    if (sessionData.date !== today) {
      return DAILY_LIMIT;
    }
    
    return Math.max(0, DAILY_LIMIT - sessionData.roundsToday);
  }, [sessionData]);

  // Clear session (for testing)
  const clearSession = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    resetSession();
  }, [resetSession]);

  const value = {
    roundsToday: sessionData.roundsToday,
    dailyLimit: DAILY_LIMIT,
    remainingRounds: getRemainingRounds(),
    hasReachedLimit: hasReachedLimit(),
    sessionId: sessionData.sessionId,
    
    // Functions
    incrementRounds,
    setRoundsCount,
    resetSession,
    clearSession,
    getRemainingRounds
  };

  return (
    <AnonymousContext.Provider value={value}>
      {children}
    </AnonymousContext.Provider>
  );
}

